import { Injectable, inject } from '@angular/core';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AUTH_CONFIG, AuthConfig } from './auth.config';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private cfg = inject<AuthConfig>(AUTH_CONFIG, { optional: true });

  private readonly _destroying$ = new Subject<void>();
  loggedIn = false;
  authenticated = false;

  private readonly defaultRolesKey = 'roles';
  private readonly defaultSilentScopes = ['openid', 'profile'];

  async init(): Promise<void> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts && accounts.length) {
      this.msalService.instance.setActiveAccount(accounts[0]);

      const tokenClaims = this.msalService.instance.getActiveAccount()
        ?.idTokenClaims as Record<string, any> | undefined;

      const expiration = tokenClaims?.['exp']
        ? new Date(tokenClaims['exp'] * 1000)
        : null;

      if (expiration && expiration < new Date()) {
        // Token abgelaufen: still über den Refresh Token erneuern statt ausloggen.
        await this.renewTokenSilently();
      }

      this.storeActiveIdToken();
    } else {
      console.warn(`couldn't initialize MSAL Service.`);
    }
  }

  async initialize(): Promise<void> {
    await this.msalService.instance.initialize();
    const accounts = this.msalService.instance.getAllAccounts();
    this.authenticated = accounts.length > 0;
    if (this.authenticated) {
      await this.init();
    }
  }

  /**
   * Renews the cached tokens silently using the refresh token. Only falls back
   * to an interactive login when the refresh token itself can no longer be used
   * (e.g. expired or revoked).
   */
  async renewTokenSilently(): Promise<AuthenticationResult | null> {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return null;
    }

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: this.cfg?.silentRequestScopes ?? this.defaultSilentScopes,
        forceRefresh: true,
      });
      this.msalService.instance.setActiveAccount(result.account);
      this.storeActiveIdToken();
      return result;
    } catch (error: unknown) {
      if (error instanceof InteractionRequiredAuthError) {
        this.login();
      } else {
        console.error('Silent token renewal failed.', error);
      }
      return null;
    }
  }

  private storeActiveIdToken(): void {
    const idToken = this.msalService.instance.getActiveAccount()?.idToken;
    if (idToken) {
      sessionStorage.setItem('token', idToken);
    }
  }

  updateLoggedInStatus(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoggedIn();
        this.checkAndSetActiveAccount();
      });
  }

  login(): void {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.loginWithPopup();
    } else {
      this.loginWithRedirect();
    }
  }

  getUsername(): string {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts?.[0]?.username ?? '';
  }

  isInAnyConfiguredGroup(): boolean {
    const claimKey = this.cfg?.rolesClaimKey ?? this.defaultRolesKey;
    const roles = this.getRolesClaim(claimKey);
    const groupNames = this.cfg?.groupNames ?? [];
    if (!Array.isArray(roles) || roles.length === 0 || groupNames.length === 0)
      return false;
    return groupNames.some((g) => roles.includes(g));
  }

  isAdmin(): boolean {
    const admin = this.cfg?.adminGroupName;
    if (!admin) return false;
    const claimKey = this.cfg?.rolesClaimKey ?? this.defaultRolesKey;
    const roles = this.getRolesClaim(claimKey);
    return Array.isArray(roles) && roles.includes(admin);
  }

  isAuthorized(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  logout(): void {
    this.msalService.logout();
  }

  destroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  // --- private helpers ---
  private getRolesClaim(claimKey: string): string[] | undefined {
    const claims = this.msalService.instance.getAllAccounts()?.[0]
      ?.idTokenClaims as Record<string, any> | undefined;
    const raw = claims?.[claimKey];

    if (Array.isArray(raw)) return raw as string[];
    if (typeof raw === 'string') return raw.split(/[ ,]+/).filter(Boolean);
    return undefined;
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private setLoggedIn(): void {
    this.loggedIn = this.msalService.instance.getAllAccounts().length > 0;
  }

  private loginWithPopup(): void {
    const req = this.msalGuardConfig.authRequest as PopupRequest | undefined;
    (req
      ? this.msalService.loginPopup(req)
      : this.msalService.loginPopup()
    ).subscribe((response: AuthenticationResult) => {
      this.msalService.instance.setActiveAccount(response.account);
    });
  }

  private loginWithRedirect(): void {
    const req = this.msalGuardConfig.authRequest as RedirectRequest | undefined;
    this.msalService.loginRedirect(req);
  }
}
